import React from 'react';
import { useLanguage } from "@/hooks/useLanguage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, AlertTriangle, AlertCircle, TrendingUp } from "lucide-react";
import { VulnerabilityItem } from '@/types/dashboard';
import { VulnerabilityListItem } from "./VulnerabilityListItem";
import { Link } from 'react-router-dom';
import { cn } from "@/lib/utils";

export type VulnerabilitySeverity = 'critical' | 'high' | 'medium' | 'low';

interface VulnerabilitiesCardProps {
  vulnerabilities: VulnerabilityItem[];
  isLoading: boolean;
  severity?: VulnerabilitySeverity;
}

export function VulnerabilitiesCard({ vulnerabilities, isLoading, severity = 'critical' }: VulnerabilitiesCardProps) {
  const { t } = useLanguage();

  const getConfig = () => {
    switch (severity) {
      case 'high':
        return {
          title: t('dashboard.high_vulnerabilities'),
          icon: AlertCircle,
          color: 'text-amber-800 dark:text-amber-600', // Brown-ish
          bgColor: 'bg-amber-800/10',
          hoverBgColor: 'group-hover:bg-amber-800/20',
          borderColor: 'hover:border-amber-800/50',
          badgeClassName: 'bg-amber-800 hover:bg-amber-900 border-amber-800 text-white',
          badgeVariant: 'outline' // Using outline + custom class for better control
        };
      case 'medium':
        return {
          title: t('dashboard.medium_vulnerabilities'),
          icon: AlertTriangle,
          color: 'text-yellow-600 dark:text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          hoverBgColor: 'group-hover:bg-yellow-500/20',
          borderColor: 'hover:border-yellow-500/50',
          badgeClassName: 'bg-yellow-500 hover:bg-yellow-600 border-yellow-500 text-black',
          badgeVariant: 'outline'
        };
      case 'low':
        return {
          title: t('dashboard.low_vulnerabilities'),
          icon: TrendingUp,
          color: 'text-emerald-600 dark:text-emerald-500',
          bgColor: 'bg-emerald-500/10',
          hoverBgColor: 'group-hover:bg-emerald-500/20',
          borderColor: 'hover:border-emerald-500/50',
          badgeClassName: 'bg-emerald-500 hover:bg-emerald-600 border-emerald-500 text-white',
          badgeVariant: 'outline'
        };
      case 'critical':
      default:
        return {
          title: t('dashboard.critical_vulnerabilities_recent'),
          icon: Shield,
          color: 'text-destructive',
          bgColor: 'bg-destructive/10',
          hoverBgColor: 'group-hover:bg-destructive/20',
          borderColor: 'hover:border-destructive/50',
          badgeClassName: '', // Default destructive badge
          badgeVariant: 'destructive'
        };
    }
  };

  const config = getConfig();
  const Icon = config.icon;

  return (
    <Card className={cn("border-border/50 h-full bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 group flex flex-col", config.borderColor)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center">
          <div className={cn("p-2 rounded-lg mr-3 transition-colors", config.bgColor, config.hoverBgColor)}>
            <Icon className={cn("h-5 w-5", config.color)} />
          </div>
          <Link to="/cve" className={cn("hover:opacity-80 transition-colors flex-1", config.color)}>
            {config.title}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 p-2 overflow-hidden">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-5 w-3/4 bg-secondary/50 animate-pulse rounded-md" />
                <div className="h-4 w-1/4 bg-secondary/30 animate-pulse rounded-md" />
              </div>
            ))}
          </div>
        ) : vulnerabilities && vulnerabilities.length > 0 ? (
          <div className="flex flex-col h-full gap-2">
            {vulnerabilities.map((vulnerability, index) => (
              <Link
                key={index}
                to={`/cve/${vulnerability.id}`}
                state={{
                  description_en: vulnerability.description,
                  description_pl: vulnerability.description_pl
                }}
                className="block hover:bg-secondary/50 rounded-lg transition-colors flex-1 min-h-0"
              >
                <VulnerabilityListItem
                  vulnerability={vulnerability}
                  severityColor={config.color}
                  Icon={Icon}
                  badgeVariant={config.badgeVariant as any}
                  badgeClassName={config.badgeClassName}
                />
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('common.no_results')}</p>
        )}
      </CardContent>
    </Card>
  );
}
